import React from 'react';
import { tipoRenovacion as tipoRenovacionList } from '../../../data/listasDesplegables';

/**
 * SectionRenovacionPack - Componente modular para el control de renovación de póliza y vinculación de Packs.
 * Corregido: Se eliminó el "transition-all duration-300" del fieldset principal que bloqueaba
 * el recálculo de altura de Flexbox y causaba colapsos visuales (recorte) al expandirse.
 */
const SectionRenovacionPack = ({ state, actions }) => {
    // Extraemos los datos necesarios de manera segura
    const { formData, packInfos = {}, billingLogic = {} } = state;
    const { 
        handleChange, 
        handlePackChange, 
        addArrayItem, 
        removeArrayItem 
    } = actions;

    // Helper para los chips de estado de la póliza general
    const renderInfoChip = () => {
        if (!formData.tipoRenovacion) return null;

        const config = {
            'No Renovable': { label: 'Póliza no renovable', class: 'bg-slate-100 text-slate-600 border-slate-200' },
            'Temporal': { label: 'Póliza en vista temporal', class: 'bg-blue-50 text-blue-600 border-blue-200' },
            'Renovable': { label: 'Póliza con gestión de renovación', class: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
        };

        const current = config[formData.tipoRenovacion];
        if (!current) return null;

        return (
            <div className={`px-3 h-[46px] rounded-xl border text-[10px] font-black uppercase flex items-center gap-2 animate-fade-in shrink-0 ${current.class}`}>
                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                    formData.tipoRenovacion === 'Renovable' ? 'bg-emerald-500' : formData.tipoRenovacion === 'Temporal' ? 'bg-blue-400' : 'bg-slate-400'
                }`}></div>
                {current.label}
            </div>
        );
    };

    return (
        <fieldset className="border border-orange-200 rounded-xl p-6 bg-orange-50/20 shadow-sm text-left animate-fade-in">
            <legend className="px-3 text-lg font-bold text-orange-600 tracking-tight">Renovación y Vínculos</legend>

            {/* FILA 1: LÓGICA DE RENOVACIÓN */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 pt-2 items-start">
                
                {/* FORMA DE RENOVACIÓN (CON INTERRUPTOR DE EXCEPCIÓN MANUAL) */}
                <div className="md:col-span-5 flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-[14px] font-bold text-slate-600">Forma de Renovación</label>
                        
                        {/* CHECKBOX PARA FORZAR EXCEPCIÓN MANUAL */}
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input 
                                type="checkbox"
                                name="manualRenovacionOverride"
                                checked={formData.manualRenovacionOverride || false}
                                onChange={handleChange}
                                className="w-3.5 h-3.5 text-orange-600 rounded border-slate-300 focus:ring-orange-500 cursor-pointer"
                            />
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-tight">Excepción</span>
                        </label>
                    </div>

                    {formData.manualRenovacionOverride ? (
                        /* SELECTOR ACTIVO CUANDO LA EXCEPCIÓN ESTÁ HABILITADA */
                        <select
                            name="formaRenovacion"
                            value={formData.formaRenovacion || 'MANUAL'}
                            onChange={handleChange}
                            className="input-round border-orange-300 font-bold text-orange-800 bg-white animate-fade-in cursor-pointer focus:border-orange-500 focus:ring-1 focus:ring-orange-100"
                        >
                            <option value="MANUAL">✋ RENOVACIÓN MANUAL</option>
                            <option value="AUTOMÁTICA">⚡ RENOVACIÓN AUTOMÁTICA</option>
                        </select>
                    ) : (
                        /* DIV ESTÁTICO DETERMINADO POR REGLAS GENERALES */
                        <div className={`px-4 py-2.5 rounded-xl border font-black text-xs h-[46px] flex items-center shadow-sm ${
                            formData.formaRenovacion === 'AUTOMÁTICA' 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                : 'bg-orange-50 text-orange-600 border-orange-200'
                        }`}>
                            <span className="mr-2 text-base">
                                {formData.formaRenovacion === 'AUTOMÁTICA' ? '⚡' : '✋'}
                            </span>
                            {formData.formaRenovacion === 'AUTOMÁTICA' ? 'RENOVACIÓN AUTOMÁTICA' : 'RENOVACIÓN MANUAL'}
                        </div>
                    )}
                    
                    <p className="text-[9px] text-slate-400 mt-2 italic font-medium px-1">
                        {formData.manualRenovacionOverride 
                            ? "Excepción activa. Seleccione el comportamiento manualmente." 
                            : "Determinada automáticamente por reglas de compañía y riesgo."}
                    </p>
                </div>

                {/* TIPO DE RENOVACIÓN: CONDICIONAL */}
                {billingLogic.showTipoRenovacion && (
                    <div className="md:col-span-7 flex flex-col animate-fade-in">
                        <label className="text-[14px] font-bold text-slate-600 mb-2">Tipo de Renovación *</label>
                        <div className="flex items-center gap-3">
                            <select 
                                name="tipoRenovacion"
                                value={formData.tipoRenovacion || ''} 
                                onChange={handleChange}
                                className="input-round border-slate-200 font-bold flex-grow focus:border-orange-500 focus:ring-1 focus:ring-orange-100"
                                required
                            >
                                <option value="">Seleccione tipo...</option>
                                {(tipoRenovacionList || []).map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                            {renderInfoChip()}
                        </div>
                    </div>
                )}
            </div>

            {/* FILA 2: ESTRUCTURA DE VÍNCULOS */}
            <div className="mt-8 pt-6 border-t border-slate-100">
                <div className="flex flex-col max-w-md">
                    <label className="text-[14px] font-bold text-slate-600 mb-2">Estructura de Vínculos</label>
                    <label className={`flex items-center justify-between cursor-pointer p-3 rounded-xl border h-[46px] transition-colors ${
                        formData.isPolizaPack ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-100' : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}>
                        <div className="flex items-center">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                                formData.isPolizaPack ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-300'
                            }`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <span className={`ml-3 text-sm font-bold uppercase tracking-tight ${
                                formData.isPolizaPack ? 'text-blue-700' : 'text-slate-400'
                            }`}>
                                Vincular Pólizas Pack
                            </span>
                        </div>
                        <input 
                            type="checkbox" 
                            name="isPolizaPack"
                            checked={formData.isPolizaPack || false} 
                            onChange={handleChange} 
                            className="sr-only peer" 
                        />
                        <div className="w-10 h-5 bg-slate-200 rounded-full relative transition-colors peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-5"></div>
                    </label>
                </div>
            </div>

            {/* LISTADO DE PÓLIZAS VINCULADAS */}
            {formData.isPolizaPack && (
                <div className="col-span-full pt-6 animate-fade-in">
                    <div className="flex flex-wrap gap-3">
                        {(formData.referenciasPolizaPack || []).map((ref, idx) => (
                            <div 
                                key={idx} 
                                className={`flex flex-col min-w-[280px] p-4 rounded-xl border transition-all duration-300 bg-white shadow-sm ${
                                    packInfos && packInfos[ref]?.found 
                                        ? 'border-emerald-200 bg-emerald-50/20' 
                                        : 'border-slate-200 focus-within:border-blue-300'
                                }`}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex-grow">
                                        <label className="text-[8px] font-black text-slate-400 uppercase block mb-1">N° de Póliza a Vincular</label>
                                        <input 
                                            type="text" 
                                            value={ref || ''} 
                                            onChange={(e) => handlePackChange(idx, e.target.value)} 
                                            className="font-mono font-bold text-blue-900 outline-none bg-transparent w-full text-base" 
                                            placeholder="Ej: 546372..."
                                        />
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={() => removeArrayItem('referenciasPolizaPack', idx)} 
                                        className="text-slate-300 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-all"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>

                                {ref?.length >= 3 && packInfos && packInfos[ref] && (
                                    <div className={`mt-3 px-2 py-1.5 rounded-lg flex items-center gap-2 border animate-fade-in ${
                                        packInfos[ref].found 
                                            ? 'bg-emerald-50 border-emerald-100 text-emerald-700' 
                                            : 'bg-amber-50 border-amber-100 text-amber-600'
                                    }`}>
                                        <div className={`w-2 h-2 rounded-full animate-pulse ${packInfos[ref].found ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                                        <span className="text-[9px] font-black uppercase tracking-tight">
                                            {packInfos[ref].found ? `VINCULADA: ${packInfos[ref].cliente}` : '⚠ PÓLIZA NO ENCONTRADA'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                        
                        <button 
                            type="button" 
                            onClick={() => addArrayItem('referenciasPolizaPack')} 
                            className="h-[75px] px-8 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/30 transition-all duration-300 flex flex-col items-center justify-center gap-1 group"
                        >
                            <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 6v12M6 12h12" />
                            </svg>
                            <span className="text-[9px] font-black uppercase tracking-widest">Añadir Vínculo</span>
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </fieldset>
    );
};

export default SectionRenovacionPack;   